
import { debounce } from 'lodash';
import { defineComponent, nextTick, ref } from 'vue';
import { useStore } from 'vuex';
import {
  IDonViResponseBase,
  IUserResponseWithRoleBase,
} from '@/core/interfaces/ApiResponses';
import { usePagination } from '@/core/hooks/use-pagination';
import { getErrorMsg, swalErrNotification, swalNotification } from '@/core/helpers/utils';
import { useRouterCustom } from '@/core/hooks/use-router-custom';
import useCapDonVi from '@/core/hooks/use-cap-don-vi';
import { useLoading } from '@/core/hooks/use-loading';
import { usePageHeader } from '@/core/hooks/usePageHeader';
import { NGUOI_DUNG } from '@/core/constants/permissions';
import { UserService } from '@/core/services/UserService';
import { DonViService } from '@/core/services/DonVi.service';
import NguoiDungTable from '@/views/crafted/pages/nguoi-dung/nguoi-dung-table/NguoiDungTable.vue';
import Pagination from '@/components/shared/pagination/Pagination.vue';
import PageContentBase from '@/layout/_shared/PageContentBase.vue';
import PageHeaderFilters from '@/layout/header/PageHeaderFilters.vue';
import FilterItem from '@/layout/_shared/FilterItem.vue';
import { useRoute } from 'vue-router';

export default defineComponent({
  name: 'nguoi-dung-list',

  components: {
    NguoiDungTable,
    Pagination,
    PageContentBase,
    PageHeaderFilters,
    FilterItem,
  },

  setup() {
    const store = useStore();
    const route = useRoute();
    const nguoiDungItems = ref<IUserResponseWithRoleBase[]>([]);
    const donViOptions = ref<IDonViResponseBase[]>([]);
    const selectedDonViId = ref<number | ''>('');
    const { push } = useRouterCustom();
    const { currentPage, totalItems, perPage, loading } = usePagination();
    const { isCompanyLevel, isUnitLevel } = useCapDonVi();
    const { startLoading, endLoading } = useLoading();

    const queryDonViId = Number(route.query.id_don_vi);
    if (Number.isFinite(queryDonViId) && queryDonViId > 0) {
      selectedDonViId.value = queryDonViId;
    }

    document.title = 'Người dùng - Đường sắt Hà Nội';

    const { searchQuery } = usePageHeader({
      title: 'Danh sách người dùng',
      breadcrumbs: [{ text: 'Người dùng' }],
      showSearch: true,
      searchPlaceholder: 'Tìm theo tên, SĐT, email...',
      addLabel: 'Thêm người dùng',
      addPermissions: [NGUOI_DUNG.TAO_MOI],
      onAdd: () => push('/quan-ly/nguoi-dung/tao-moi'),
    });

    return {
      nguoiDungItems,
      donViOptions,
      selectedDonViId,
      currentPage,
      totalItems,
      perPage,
      loading,
      push,
      searchQuery,
      isCompanyLevel,
      isUnitLevel,
      startLoading,
      endLoading,
      currentUserDepartmentId: store.getters.currentUser?.departmentId as number,
    };
  },

  async mounted() {
    await this.startLoading('Đang tải dữ liệu');
    try {
      await this.initDonViFilter();
      await this.fetchData(false);
    } finally {
      await this.endLoading();
    }
  },

  watch: {
    async currentPage() {
      await this.fetchData();
    },

    async perPage() {
      if (this.currentPage === 1) {
        await this.fetchData();
        return;
      }
      this.currentPage = 1;
    },

    async selectedDonViId() {
      if (this.isUnitLevel) return;

      if (this.currentPage === 1) {
        await this.fetchData();
        return;
      }
      this.currentPage = 1;
    },

    searchQuery: debounce(function () {
      nextTick().then(async () => {
        if (this.currentPage === 1) {
          await this.fetchData();
          return;
        }
        this.currentPage = 1;
      });
    }, 500),
  },

  methods: {
    async initDonViFilter() {
      try {
        if (this.isUnitLevel) {
          this.selectedDonViId = this.currentUserDepartmentId;
          const {
            data: { data },
          } = await DonViService.fetchCurrentDepartmentById(
            this.currentUserDepartmentId
          );
          this.donViOptions = data ? [data] : [];
          return;
        }

        const {
          data: { data },
        } = await DonViService.fetchAllDepartments();
        this.donViOptions = data || [];
      } catch (error) {
        await swalNotification(
          getErrorMsg(error, 'Có lỗi khi tải danh sách đơn vị'),
          'error'
        );
      }
    },

    async fetchData(showOverlay = true) {
      try {
        if (showOverlay) {
          await this.startLoading('Đang tải dữ liệu');
        }

        this.loading = true;
        const {
          data: {
            data: { data, total },
          },
        } = await UserService.list(
          this.currentPage,
          this.perPage,
          this.searchQuery,
          this.selectedDonViId || undefined
        );

        this.nguoiDungItems = data;
        this.totalItems = total;
      } catch (error) {
        await swalErrNotification(
          error,
          'Có lỗi xảy ra khi lấy danh sách người dùng'
        );
      } finally {
        this.loading = false;

        if (showOverlay) {
          await this.endLoading();
        }
      }
    },
  },
});
